"use client";

import log from "logbeacon";
import { trackEvent } from "@/src/utils/tracking";
import { getOnboardingV3Session } from "@/src/hooks/onboarding/onboardingV3SessionStorage";
import {
  resolveOnboardingPlanUnlock,
  type ResolveOnboardingFirstPaywallDeps,
} from "@/src/hooks/onboarding/resolveOnboardingFirstPaywallOutcome";
import { EdgenModalCreateAble } from "@/src/components/EdgenModal";
import { callEdgenModalWhenRootReadyAsync } from "@/src/components/EdgenModal/waitForEdgenModalRoot";
import { PlanOverviewModalContent } from "./PlanOverviewModalContent";
import { ONBOARDING_PLAN_OVERVIEW_MODAL_PROPS } from "./planOverviewModalShell";
import type {
  OpenOnboardingPlanOverviewModalParams,
  PlanOverviewModalResult,
} from "./types";

let pendingPlanOverviewModal: Promise<PlanOverviewModalResult> | null = null;

export type OpenOnboardingPlanOverviewModalContext =
  ResolveOnboardingFirstPaywallDeps;

/**
 * UI10 Plan Modal。Unlock：先 navigate Brief + 关本层；
 * 会员直接承接 draft（同 Free plan offer CTA），非会员再开 Paywall。
 */
export const openOnboardingPlanOverviewModal = (
  params: OpenOnboardingPlanOverviewModalParams & {
    paywallDeps: OpenOnboardingPlanOverviewModalContext;
  },
): Promise<PlanOverviewModalResult> => {
  if (pendingPlanOverviewModal) {
    return pendingPlanOverviewModal;
  }

  const isPC = params.isPC ?? false;
  const session = getOnboardingV3Session();
  const planPreview = params.planPreview ?? session?.planPreview ?? null;

  pendingPlanOverviewModal = callEdgenModalWhenRootReadyAsync(
    () =>
      new Promise<PlanOverviewModalResult>((resolve) => {
        let settled = false;
        let modalInstance: Promise<boolean>;

        const finish = (result: PlanOverviewModalResult) => {
          if (settled) {
            return;
          }
          settled = true;
          pendingPlanOverviewModal = null;
          EdgenModalCreateAble.end(modalInstance, false);
          resolve(result);
        };

        const handleUnlock = async () => {
          if (!planPreview?.draftId) {
            params.onMissingPlanPreview?.();
            return;
          }

          await params.onUnlock?.();

          params.onNavigateToBrief?.();
          finish({ action: "unlock" });

          trackEvent("ob_plan_view", {
            goal: getOnboardingV3Session()?.selectedGoalType,
          });

          try {
            await resolveOnboardingPlanUnlock({
              ...params.paywallDeps,
              isPC,
            });
          } catch (error) {
            log.warn("[onboarding] paywall outcome failed", error);
          }
        };

        modalInstance = EdgenModalCreateAble.call({
          children: (
            <PlanOverviewModalContent
              isPC={isPC}
              planPreview={planPreview}
              planPreviewLoading={!planPreview?.draftId}
              onBack={() => finish({ action: "back" })}
              onUnlock={() => {
                void handleUnlock();
              }}
            />
          ),
          ...ONBOARDING_PLAN_OVERVIEW_MODAL_PROPS,
        });
      }),
  ).catch((error) => {
    pendingPlanOverviewModal = null;
    throw error;
  });

  return pendingPlanOverviewModal;
};
